import type {
  CustomerIdentifierStatus,
  CustomerIdentifierType,
  CustomerStatus,
} from '../customers/customers.dto.js';
import type { EmployeeStatus, EmploymentType } from '../employees/employees.dto.js';
import type {
  CoverShipmentDepartedEventPayload,
  CoverShipmentEvidenceUpdatedEventPayload,
  CoverShipmentSavedEventPayload,
  CoverShipmentVoidedEventPayload,
} from './cover-shipments/cover-shipments-sync.dto.js';
import {
  coverShipmentSyncEntityTypeValues,
  coverShipmentSyncEventTypeValues,
} from './cover-shipments/cover-shipments-sync.dto.js';
import type {
  InboundReceiptCanceledEventPayload,
  InboundReceiptComparisonReviewedEventPayload,
  InboundReceiptConfirmedEventPayload,
  InboundReceiptInventoryPostedEventPayload,
  InboundReceiptInventoryPostingReversedEventPayload,
  InboundReceiptOfficialStateReplacedEventPayload,
} from './inbound-receipts/inbound-receipts-sync.dto.js';
import {
  inboundReceiptSyncEntityTypeValues,
  inboundReceiptSyncEventTypeValues,
} from './inbound-receipts/inbound-receipts-sync.dto.js';
import type { IncomingTransferUpsertedEventPayload } from './incoming-transfers/incoming-transfers-sync.dto.js';
import {
  incomingTransferSyncEntityTypeValues,
  incomingTransferSyncEventTypeValues,
} from './incoming-transfers/incoming-transfers-sync.dto.js';
import type {
  PurchaseCanceledEventPayload,
  PurchaseCommittedEventPayload,
  PurchaseLimitLedgerEntryRecordedEventPayload,
  PurchaseNoteAddedEventPayload,
  PurchasePrintStatusUpdatedEventPayload,
} from './purchases/purchases-sync.dto.js';
import {
  purchaseSyncEntityTypeValues,
  purchaseSyncEventTypeValues,
} from './purchases/purchases-sync.dto.js';
import type {
  ShipmentDriverAssignmentSyncPayload,
  ShipmentDraftPayload,
  ShipmentLogisticsShellReversedEventPayload,
  ShipmentLogisticsShellUpsertedEventPayload,
  ShipmentSavedEventPayload,
  ShipmentScaleTicketEventPayload,
  ShipmentWeighEventSyncPayload,
  ShipmentVoidedEventPayload,
} from './shipments/shipments-sync.dto.js';
import {
  shipmentSyncEntityTypeValues,
  shipmentSyncEventTypeValues,
} from './shipments/shipments-sync.dto.js';
import type { SourcePreparationRequestStatusUpdatedEventPayload } from './source-preparation/source-preparation-sync.dto.js';
import {
  sourcePreparationSyncEntityTypeValues,
  sourcePreparationSyncEventTypeValues,
} from './source-preparation/source-preparation-sync.dto.js';
import type { HoldingUnitSyncEventType, HoldingUnitSyncPayload } from './holding-units-sync.dto.js';
import {
  holdingUnitSyncEntityTypeValues,
  holdingUnitSyncEventTypeValues,
} from './holding-units-sync.dto.js';
import type { SyncDeliveryErrorCode, SyncDeliveryErrorDetails } from './sync-delivery-errors.js';

export const employeeSyncEventTypeValues = ['employeeCreated', 'employeeUpdated'] as const;
export type EmployeeSyncEventType = (typeof employeeSyncEventTypeValues)[number];

export const customerSyncEventTypeValues = [
  'customerCreated',
  'customerUpdated',
  'customerDeactivated',
] as const;
export type CustomerSyncEventType = (typeof customerSyncEventTypeValues)[number];

export const customerIdentifierSyncEventTypeValues = [
  'customerIdentifierAdded',
  'customerIdentifierUpdated',
  'customerIdentifierDeactivated',
  'customerIdentifierDefaulted',
] as const;
export type CustomerIdentifierSyncEventType =
  (typeof customerIdentifierSyncEventTypeValues)[number];

export const purchaseReassignmentAuditSyncEventTypeValues = [
  'purchaseReassignmentAuditRecorded',
] as const;
export type PurchaseReassignmentAuditSyncEventType =
  (typeof purchaseReassignmentAuditSyncEventTypeValues)[number];

export const syncEventTypeValues = [
  ...employeeSyncEventTypeValues,
  ...customerSyncEventTypeValues,
  ...customerIdentifierSyncEventTypeValues,
  ...purchaseReassignmentAuditSyncEventTypeValues,
  ...purchaseSyncEventTypeValues,
  ...shipmentSyncEventTypeValues,
  ...sourcePreparationSyncEventTypeValues,
  ...inboundReceiptSyncEventTypeValues,
  ...incomingTransferSyncEventTypeValues,
  ...coverShipmentSyncEventTypeValues,
  ...holdingUnitSyncEventTypeValues,
] as const;
export type SyncEventType = (typeof syncEventTypeValues)[number];

export const syncEntityTypeValues = [
  'employee',
  'customer',
  'customerIdentifier',
  'purchaseReassignmentAudit',
  ...purchaseSyncEntityTypeValues,
  ...shipmentSyncEntityTypeValues,
  ...sourcePreparationSyncEntityTypeValues,
  ...inboundReceiptSyncEntityTypeValues,
  ...incomingTransferSyncEntityTypeValues,
  ...coverShipmentSyncEntityTypeValues,
  ...holdingUnitSyncEntityTypeValues,
] as const;
export type SyncEntityType = (typeof syncEntityTypeValues)[number];
export const syncPullEntityTypeValues = [
  'employee',
  'customer',
  'customerIdentifier',
  'purchaseReassignmentAudit',
  ...purchaseSyncEntityTypeValues,
  ...shipmentSyncEntityTypeValues,
  ...sourcePreparationSyncEntityTypeValues,
  ...inboundReceiptSyncEntityTypeValues,
  ...incomingTransferSyncEntityTypeValues,
  ...coverShipmentSyncEntityTypeValues,
] as const;
export type SyncPullEntityType = (typeof syncPullEntityTypeValues)[number];

export interface SyncClientIdentity {
  stationId: string;
  deviceId: string;
  deviceClaimId: string;
}

export interface EmployeeSyncPayload {
  id: string;
  companyId: string;
  storeId: string;
  locationId: string | null;
  userId: string | null;
  employeeNumber: string | null;
  timezone: string | null;
  firstName: string;
  lastName: string;
  displayName: string;
  status: EmployeeStatus;
  hireDate: string | null; // ISO date (YYYY-MM-DD)
  terminationDate: string | null; // ISO date (YYYY-MM-DD)
  employmentType: EmploymentType | null;
  assignments?: EmployeeAssignmentSyncPayload[];
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface EmployeeAssignmentSyncPayload {
  companyId: string;
  employeeId: string;
  storeId: string;
  locationId: string | null;
  isPrimary: boolean;
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface EmployeeSyncEventPayload {
  employee: EmployeeSyncPayload;
}

export interface CustomerSyncPayload {
  id: string;
  companyId: string;
  displayName: string;
  status: CustomerStatus;
  isPublic: boolean;
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface CustomerSyncEventPayload {
  customer: CustomerSyncPayload;
}

export interface CustomerIdentifierSyncPayload {
  id: string;
  companyId: string;
  customerId: string;
  type: CustomerIdentifierType;
  valueHash: string;
  valueMasked: string;
  status: CustomerIdentifierStatus;
  isDefault: boolean;
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface CustomerIdentifierSyncEventPayload {
  customerIdentifier: CustomerIdentifierSyncPayload;
}

export interface PurchaseReassignmentAuditSyncPayload {
  id: string;
  companyId: string;
  actorUserId: string;
  action: string;
  purchaseId: string | null;
  receiptNumber: string;
  occurredAt: string; // ISO datetime
  createdAt: string; // ISO datetime
}

export interface PurchaseReassignmentAuditSyncEventPayload {
  purchaseReassignmentAudit: PurchaseReassignmentAuditSyncPayload;
}

export interface BaseSyncEvent {
  eventId: string;
  entityType: SyncEntityType;
  entityId: string;
  eventType: SyncEventType;
  occurredAt: string; // ISO datetime
  source?: string | null;
}

export interface EmployeeSyncEvent extends BaseSyncEvent {
  entityType: 'employee';
  eventType: EmployeeSyncEventType;
  payload: EmployeeSyncEventPayload;
}

export interface CustomerSyncEvent extends BaseSyncEvent {
  entityType: 'customer';
  eventType: CustomerSyncEventType;
  payload: CustomerSyncEventPayload;
}

export interface CustomerIdentifierSyncEvent extends BaseSyncEvent {
  entityType: 'customerIdentifier';
  eventType: CustomerIdentifierSyncEventType;
  payload: CustomerIdentifierSyncEventPayload;
}

export interface PurchaseReassignmentAuditSyncEvent extends BaseSyncEvent {
  entityType: 'purchaseReassignmentAudit';
  eventType: PurchaseReassignmentAuditSyncEventType;
  payload: PurchaseReassignmentAuditSyncEventPayload;
}

export interface PurchaseCommittedSyncEvent extends BaseSyncEvent {
  entityType: 'purchase';
  eventType: 'purchaseCommitted';
  payload: PurchaseCommittedEventPayload;
}

export interface PurchaseCanceledSyncEvent extends BaseSyncEvent {
  entityType: 'purchase';
  eventType: 'purchaseCanceled';
  payload: PurchaseCanceledEventPayload;
}

export interface PurchasePrintStatusUpdatedSyncEvent extends BaseSyncEvent {
  entityType: 'purchase';
  eventType: 'purchasePrintStatusUpdated';
  payload: PurchasePrintStatusUpdatedEventPayload;
}

export interface PurchaseNoteAddedSyncEvent extends BaseSyncEvent {
  entityType: 'purchase';
  eventType: 'purchaseNoteAdded';
  payload: PurchaseNoteAddedEventPayload;
}

export interface PurchaseLimitLedgerEntryRecordedSyncEvent extends BaseSyncEvent {
  entityType: 'purchaseLimitLedgerEntry';
  eventType: 'purchaseLimitLedgerEntryRecorded';
  payload: PurchaseLimitLedgerEntryRecordedEventPayload;
}

export interface ShipmentDraftCreatedSyncEvent extends BaseSyncEvent {
  entityType: 'shipment';
  eventType: 'shipmentCreated';
  payload: ShipmentDraftPayload;
}

export interface ShipmentDraftUpdatedSyncEvent extends BaseSyncEvent {
  entityType: 'shipment';
  eventType: 'shipmentUpdated';
  payload: ShipmentDraftPayload;
}

export interface ShipmentSavedSyncEvent extends BaseSyncEvent {
  entityType: 'shipment';
  eventType: 'shipmentSaved';
  payload: ShipmentSavedEventPayload;
}

export interface ShipmentVoidedSyncEvent extends BaseSyncEvent {
  entityType: 'shipment';
  eventType: 'shipmentVoided';
  payload: ShipmentVoidedEventPayload;
}

export interface ShipmentScaleTicketAddedSyncEvent extends BaseSyncEvent {
  entityType: 'shipment';
  eventType: 'shipmentScaleTicketAdded';
  payload: ShipmentScaleTicketEventPayload;
}

export interface ShipmentScaleTicketUpdatedSyncEvent extends BaseSyncEvent {
  entityType: 'shipment';
  eventType: 'shipmentScaleTicketUpdated';
  payload: ShipmentScaleTicketEventPayload;
}

export interface ShipmentScaleTicketVoidedSyncEvent extends BaseSyncEvent {
  entityType: 'shipment';
  eventType: 'shipmentScaleTicketVoided';
  payload: ShipmentScaleTicketEventPayload;
}

export interface ShipmentWeighEventAddedSyncEvent extends BaseSyncEvent {
  entityType: 'shipment';
  eventType: 'shipmentWeighEventAdded';
  payload: ShipmentWeighEventSyncPayload;
}

export interface ShipmentWeighEventUpdatedSyncEvent extends BaseSyncEvent {
  entityType: 'shipment';
  eventType: 'shipmentWeighEventUpdated';
  payload: ShipmentWeighEventSyncPayload;
}

export interface ShipmentWeighEventVoidedSyncEvent extends BaseSyncEvent {
  entityType: 'shipment';
  eventType: 'shipmentWeighEventVoided';
  payload: ShipmentWeighEventSyncPayload;
}

export interface ShipmentDriverAssignmentUpdatedSyncEvent extends BaseSyncEvent {
  entityType: 'shipment';
  eventType: 'shipmentDriverAssignmentUpdated';
  payload: ShipmentDriverAssignmentSyncPayload;
}

export interface ShipmentLogisticsShellUpsertedSyncEvent extends BaseSyncEvent {
  entityType: 'shipmentLogisticsShell';
  eventType: 'shipmentLogisticsShellUpserted';
  payload: ShipmentLogisticsShellUpsertedEventPayload;
}

export interface ShipmentLogisticsShellReversedSyncEvent extends BaseSyncEvent {
  entityType: 'shipmentLogisticsShell';
  eventType: 'shipmentLogisticsShellReversed';
  payload: ShipmentLogisticsShellReversedEventPayload;
}

export interface SourcePreparationRequestStatusUpdatedSyncEvent extends BaseSyncEvent {
  entityType: 'sourcePreparationRequest';
  eventType: 'sourcePreparationRequestStatusUpdated';
  payload: SourcePreparationRequestStatusUpdatedEventPayload;
}

export interface InboundReceiptConfirmedSyncEvent extends BaseSyncEvent {
  entityType: 'inboundReceipt';
  eventType: 'inboundReceiptConfirmedV2';
  payload: InboundReceiptConfirmedEventPayload;
}

export interface InboundReceiptCanceledSyncEvent extends BaseSyncEvent {
  entityType: 'inboundReceipt';
  eventType: 'inboundReceiptCanceledV2';
  payload: InboundReceiptCanceledEventPayload;
}

export interface InboundReceiptOfficialStateReplacedSyncEvent extends BaseSyncEvent {
  entityType: 'inboundReceipt';
  eventType: 'inboundReceiptOfficialStateReplacedV2';
  payload: InboundReceiptOfficialStateReplacedEventPayload;
}

export interface InboundReceiptComparisonReviewedSyncEvent extends BaseSyncEvent {
  entityType: 'inboundReceipt';
  eventType: 'inboundReceiptComparisonReviewedV2';
  payload: InboundReceiptComparisonReviewedEventPayload;
}

export interface InboundReceiptInventoryPostedSyncEvent extends BaseSyncEvent {
  entityType: 'inboundReceipt';
  eventType: 'inboundReceiptInventoryPostedV2';
  payload: InboundReceiptInventoryPostedEventPayload;
}

export interface InboundReceiptInventoryPostingReversedSyncEvent extends BaseSyncEvent {
  entityType: 'inboundReceipt';
  eventType: 'inboundReceiptInventoryPostingReversedV2';
  payload: InboundReceiptInventoryPostingReversedEventPayload;
}

export interface IncomingTransferUpsertedSyncEvent extends BaseSyncEvent {
  entityType: 'incomingTransfer';
  eventType: 'incomingTransferUpserted';
  payload: IncomingTransferUpsertedEventPayload;
}

export interface CoverShipmentSavedSyncEvent extends BaseSyncEvent {
  entityType: 'coverShipment';
  eventType: 'coverShipmentSaved';
  payload: CoverShipmentSavedEventPayload;
}

export interface CoverShipmentVoidedSyncEvent extends BaseSyncEvent {
  entityType: 'coverShipment';
  eventType: 'coverShipmentVoided';
  payload: CoverShipmentVoidedEventPayload;
}

export interface CoverShipmentEvidenceUpdatedSyncEvent extends BaseSyncEvent {
  entityType: 'coverShipment';
  eventType: 'coverShipmentEvidenceUpdated';
  payload: CoverShipmentEvidenceUpdatedEventPayload;
}

export interface CoverShipmentDepartedSyncEvent extends BaseSyncEvent {
  entityType: 'coverShipment';
  eventType: 'coverShipmentDeparted';
  payload: CoverShipmentDepartedEventPayload;
}

export interface HoldingUnitSyncEvent extends BaseSyncEvent {
  entityType: 'holdingUnit';
  eventType: HoldingUnitSyncEventType;
  payload: HoldingUnitSyncPayload;
}

export type SyncEvent =
  | EmployeeSyncEvent
  | CustomerSyncEvent
  | CustomerIdentifierSyncEvent
  | PurchaseReassignmentAuditSyncEvent
  | PurchaseCommittedSyncEvent
  | PurchaseCanceledSyncEvent
  | PurchasePrintStatusUpdatedSyncEvent
  | PurchaseNoteAddedSyncEvent
  | PurchaseLimitLedgerEntryRecordedSyncEvent
  | ShipmentDraftCreatedSyncEvent
  | ShipmentDraftUpdatedSyncEvent
  | ShipmentSavedSyncEvent
  | ShipmentVoidedSyncEvent
  | ShipmentScaleTicketAddedSyncEvent
  | ShipmentScaleTicketUpdatedSyncEvent
  | ShipmentScaleTicketVoidedSyncEvent
  | ShipmentWeighEventAddedSyncEvent
  | ShipmentWeighEventUpdatedSyncEvent
  | ShipmentWeighEventVoidedSyncEvent
  | ShipmentDriverAssignmentUpdatedSyncEvent
  | ShipmentLogisticsShellUpsertedSyncEvent
  | ShipmentLogisticsShellReversedSyncEvent
  | SourcePreparationRequestStatusUpdatedSyncEvent
  | InboundReceiptConfirmedSyncEvent
  | InboundReceiptCanceledSyncEvent
  | InboundReceiptOfficialStateReplacedSyncEvent
  | InboundReceiptComparisonReviewedSyncEvent
  | InboundReceiptInventoryPostedSyncEvent
  | InboundReceiptInventoryPostingReversedSyncEvent
  | IncomingTransferUpsertedSyncEvent
  | CoverShipmentSavedSyncEvent
  | CoverShipmentVoidedSyncEvent
  | CoverShipmentEvidenceUpdatedSyncEvent
  | CoverShipmentDepartedSyncEvent
  | HoldingUnitSyncEvent;

export type SyncPushEvent = SyncEvent;

export interface SyncPushRequest {
  client: SyncClientIdentity;
  events: SyncPushEvent[];
}

export type SyncEventResultStatus = 'ok' | 'error';
export type SyncCloudApplyStatus = 'projected' | 'ingested_projection_pending';

export interface SyncEventSuccessResult {
  eventId: string;
  status: 'ok';
  cloudApplyStatus?: SyncCloudApplyStatus;
}

export interface SyncEventErrorResult {
  eventId: string;
  status: 'error';
  errorCode: SyncDeliveryErrorCode;
  message?: string;
  details?: SyncDeliveryErrorDetails;
}

export type SyncEventResult = SyncEventSuccessResult | SyncEventErrorResult;

export interface SyncPushResponse {
  results: SyncEventResult[];
}

export interface SyncPullRequest {
  client: SyncClientIdentity;
  cursor?: string | null; // Opaque cursor
  entityTypes?: SyncPullEntityType[];
}

export type SyncPullItem = Exclude<SyncEvent, HoldingUnitSyncEvent>;

export interface SyncPullResponse {
  items: SyncPullItem[];
  nextCursor: string | null; // Opaque cursor
}

export interface SyncAckRequest {
  client: SyncClientIdentity;
  cursor?: string | null; // Opaque cursor
  eventIds?: string[];
}

export interface SyncAckResponse {
  accepted: number;
  rejected: number;
}
